import { useState, useEffect } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

// How long to wait for PostHog before assuming it won't respond.
const POSTHOG_TIMEOUT_MS = 15000;

/**
 * Shared timeout guard for the flag hooks below.
 *
 * The native PostHog React hooks return `undefined` until flags load and never
 * time out — so if PostHog is blocked (ad-blocker) or the network fails, a
 * consumer gating a spinner on `=== undefined` would hang forever. This returns
 * true once POSTHOG_TIMEOUT_MS has elapsed while still loading, letting callers
 * fall back to a definite "disabled" value instead of loading indefinitely.
 *
 * @param {boolean} isLoading - Whether the flag is still unresolved
 * @returns {boolean} - True once the timeout has elapsed during loading
 */
const useFlagTimeout = (isLoading) => {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    // Reset for this loading cycle (e.g. flagKey changed), then arm the timeout.
    setTimedOut(false);
    const timeoutId = setTimeout(() => setTimedOut(true), POSTHOG_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  return timedOut;
};

/**
 * Thin wrapper over PostHog's `useFeatureFlagEnabled` that adds a timeout
 * fallback: returns the boolean flag value once loaded, `undefined` while
 * loading, and `false` if PostHog doesn't respond within POSTHOG_TIMEOUT_MS.
 *
 * A falsy `flagKey` (e.g. no applicable flag yet) resolves to `false` — disabled,
 * never loading — so callers can pass a key that's only known asynchronously.
 *
 * @param {string} [flagKey] - The feature flag key to check
 * @returns {boolean|undefined} - true/false when resolved, undefined while loading
 */
const usePostHogFeatureFlag = (flagKey) => {
  const enabled = useFeatureFlagEnabled(flagKey);
  const timedOut = useFlagTimeout(!!flagKey && enabled === undefined);

  if (!flagKey) {
    return false;
  }
  if (enabled !== undefined) {
    return enabled;
  }
  return timedOut ? false : undefined;
};

export default usePostHogFeatureFlag;
